import React, { useState, useMemo, useRef, useEffect } from "react";
import { Search, Clear } from "@mui/icons-material";
import Toast from "./Toast.jsx";
import NoContent from "./NoContent";
import Paginator from "./shared/Paginator.jsx";
import Avatar from "./shared/Avatar.jsx";

import { formatProjectDate } from "../date_utils.js";
import { usePagedList } from "../hooks/usePagedList.js";

export default function List({
  data, // The data to be displayed. Expected type is Object[]
  itemsPerPage = 8, // Default items are 8 if not provided
  itemNameKey, // The key used to display an item's name
  itemExcerptKey, // The key used to identify the item's description
  itemCategoryKeys, // The keys used to identify an items categories
  itemFields, // List of objects for identifying an item's field entry. The field label and display type must also be given
  listFilterKey, // They key used to identify an item's category for filtering
  listFilterVals, // The possible values in the given listFilter key
  advancedFilterKeys, // The keys used to get advanced filter values from
  dateFilterKey,
  contributors, // SPECIAL FIELD: An object containing the keys for identifying participants in an item and a label to identify them to
  priorityKey, // SPECIAL FIELD: A key used to identify an item's priority
  onItemClick, // A function that will be executed when a user selects a list item (optional)
}) {
  const listFilters = ["All"].concat(listFilterVals ?? []);

  const advancedFilterValues = useRef(new Map());

  const [query, setQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState(listFilters[0]);
  const [advancedFilters, setAdvancedFilters] = useState(new Map());
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const filteredData = useMemo(
    () =>
      data.filter((item) => {
        if (activeFilter === listFilters[0] && query === "" && advancedFilters.size < 1)
          return true;

        const inDateFilter = () => {
          if (
            !Array.from(advancedFilters.keys()).includes("startDate") &&
            !Array.from(advancedFilters.keys()).includes("endDate")
          )
            return true;

          const filterStartDate = Array.from(advancedFilters.keys()).includes("startDate")
            ? new Date(advancedFilters.get("startDate")[0])
            : null;
          const filterEndDate = Array.from(advancedFilters.keys()).includes("endDate")
            ? new Date(advancedFilters.get("endDate")[0])
            : null;
          const projectDate = new Date(formatProjectDate(item[dateFilterKey]));

          if (filterStartDate && filterEndDate) {
            return projectDate >= filterStartDate && projectDate <= filterEndDate;
          } else if (filterStartDate && !filterEndDate) {
            return projectDate >= filterStartDate;
          } else if (!filterStartDate && filterEndDate) {
            return projectDate <= filterEndDate;
          }

          return true;
        };

        const inAdvancedFilter = () => {
          const checkboxFilters = new Map(advancedFilters);
          checkboxFilters.delete("startDate");
          checkboxFilters.delete("endDate");

          if (checkboxFilters.size < 1) return true;

          for (const f of checkboxFilters.keys()) {
            for (const v of checkboxFilters.get(f)) {
              if (item[f] === v) {
                return true;
              }
            }
          }

          return false;
        };
        const inFilter =
          activeFilter != listFilters[0]
            ? item[listFilterKey ?? activeFilter] === activeFilter
            : true;
        const inSearchQuery =
          query === ""
            ? true
            : (item[itemNameKey]?.toLowerCase() ?? "").includes(query?.toLowerCase());

        return inFilter && inSearchQuery && inDateFilter() && inAdvancedFilter();
      }),
    [data, activeFilter, query, advancedFilters],
  );

  const { currentPage, setCurrentPage, pageItems, pageCount, pagesToDisplay } = usePagedList(
    filteredData,
    itemsPerPage,
  );

  const mappedData = useMemo(() => {
    return pageItems.map((item, index) => {
      return (
        <li key={`${item}-${index}`} className="list-item-container">
          <div className="list-item">
            {!onItemClick ? (
              <h2 className="list-item-title">{item[itemNameKey]}</h2>
            ) : (
              <button onClick={() => onItemClick(pageItems[index])}>
                <h2 className="list-item-title">{item[itemNameKey]}</h2>
              </button>
            )}

            {/* For displaying the item's categories if exists */}
            {itemCategoryKeys && (
              <span className="item-category">{getItemCategories(itemCategoryKeys, item)}</span>
            )}

            {/* For displaying item's excerpt if exists */}
            {itemExcerptKey && <p className="item-excerpt">{item[itemExcerptKey]}</p>}

            {/* For displaying the item's fields if they exist */}
            {itemFields && (
              <div className="fields">
                {itemFields.map((field, index) => {
                  if (!item[field.key]) return <></>;

                  // Handle field if value is not singular
                  return (
                    <span key={`${item}-${field.key}-${index}`} className="field-item-span">
                      <p>{field.label}:</p>
                      {typeof item[field.key] === "object" ? (
                        item[field.key].map((val, index) => {
                          if (index > 3) return <></>;

                          if (index <= 2) {
                            return (
                              <p key={val} className={`${getTypeClass(field.type)}`}>
                                {val}
                              </p>
                            );
                          }

                          return (
                            <p key={val} className={`${getTypeClass(field.type)}`}>
                              +{item[field.key].length - index}
                            </p>
                          );
                        })
                      ) : (
                        <p className={`${getTypeClass(field.type)}`}>{item[field.key]}</p>
                      )}
                    </span>
                  );
                })}
              </div>
            )}

            {/* For displaying item's contributors if exists */}
            {contributors && (
              <span className="contributor-span">
                {item[contributors.key].map((c) => (
                  <Avatar key={c} name={c} className="contributor-circle" />
                ))}
                <p>{contributors.label}</p>
              </span>
            )}

            {/* For displaying item's priority if exists */}
          </div>
        </li>
      );
    });
  }, [pageItems, data]);

  useEffect(() => {
    if (advancedFilterKeys) {
      const filters = new Map();

      for (const project of data) {
        for (const key of advancedFilterKeys) {
          const vals = filters.get(key) ?? [];

          if (!vals.includes(project[key])) {
            vals.push(project[key]);
          }

          filters.set(key, vals);
        }
      }

      advancedFilterValues.current = new Map(filters);
    }
  }, []);

  return (
    <div className="list">
      <div className="list-query-section">
        <span>
          <Search />
          <input type="text" onChange={(e) => setQuery(e.target.value)} />
        </span>
        <button onClick={() => setShowAdvancedFilters(true)}>Advanced Filters</button>
      </div>

      <ul className="list-filter-nav">
        {listFilters.map((filter) => (
          <li key={filter}>
            <button
              onClick={() => setActiveFilter(filter)}
              className={`${filter === activeFilter.valueOf() ? "list-filter-item-selected" : "list-filter-item"}`}
            >
              {filter}
            </button>
          </li>
        ))}
      </ul>

      {mappedData.length > 0 ? (
        <>
          <ul className="list-container">{mappedData}</ul>

          <Paginator
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            pageCount={pageCount}
            pagesToDisplay={pagesToDisplay}
          />
        </>
      ) : (
        <NoContent message={"No results found."} />
      )}

      {showAdvancedFilters && (
        <AdvancedFilters
          filters={advancedFilterValues.current}
          activeFilters={advancedFilters}
          onApply={(aFilter) => {
            setShowAdvancedFilters(false);
            setAdvancedFilters(() => new Map(aFilter));
          }}
          enableDateFilters={dateFilterKey}
          onClose={() => setShowAdvancedFilters(false)}
        />
      )}
    </div>
  );
}

function AdvancedFilters({ filters, activeFilters, enableDateFilters, onApply, onClose }) {
  const [showToast, setShowToast] = useState(false);
  const [query, setQuery] = useState("");

  const listFilters = new Map(filters);
  const selectedFilters = activeFilters ?? new Map();

  const mappedFilters = useMemo(
    () =>
      Array.from(listFilters.keys())
        .filter((k) => {
          if (query === "") return true;
          return k.includes(query);
        })
        .map((k) => (
          <li key={k}>
            <p className="a-filter-entry-name">
              {k[0].toUpperCase()}
              {k.slice(1)}
            </p>

            <ul className="a-filter-list">
              {listFilters.get(k).map((v) => {
                const isToggled = selectedFilters.get(k)?.includes(v) ? true : false;
                const valueId = `${k}-${v}`;

                return (
                  <li key={valueId} className="a-filter-list-items">
                    <input
                      name={valueId}
                      id={valueId}
                      type="checkbox"
                      onChange={() => toggleFilter(k, v)}
                      defaultChecked={isToggled}
                    />
                    <label htmlFor={valueId}>{v}</label>
                  </li>
                );
              })}
            </ul>
          </li>
        )),
    [query],
  );

  const toggleFilter = (k, v) => {
    let filterVals = selectedFilters.get(k) ?? [];

    // Condition for date filters
    if (k === "startDate" || k === "endDate") {
      filterVals.length = 0;

      if (v != "") {
        filterVals.push(v);
      }
    } else {
      if (filterVals.includes(v)) {
        filterVals.splice(filterVals.indexOf(v), 1);
      } else {
        filterVals.push(v);
      }
    }

    selectedFilters.set(k, filterVals);

    if (filterVals.length < 1) selectedFilters.delete(k);

    if (selectedFilters.has("startDate") && selectedFilters.has("endDate")) {
      const startDate = new Date(selectedFilters.get("startDate"));
      const endDate = new Date(selectedFilters.get("endDate"));

      if (startDate > endDate) {
        selectedFilters.delete("startDate");
        selectedFilters.delete("endDate");
        setShowToast(true);
      }
    }
  };

  return (
    <>
      <div className="a-filters-bg">
        <div className="a-filters">
          <section className="a-filter-header">
            <p>Filters</p>
            <span className="a-filter-query">
              <Search />
              <input
                type="text"
                placeholder="Search by filter criteria"
                onChange={(e) => setQuery(e.target.value)}
              />
            </span>
            <button onClick={() => onClose()}>
              <Clear />
            </button>
          </section>

          <section>
            {mappedFilters.length < 1 ? (
              <NoContent message={"No available filters."} />
            ) : (
              <ul className="a-filter-entries">{mappedFilters}</ul>
            )}
          </section>

          {enableDateFilters && (
            <section>
              <p
                style={{
                  paddingLeft: "1.5rem",
                  fontWeight: "bold",
                  margin: "0",
                  marginBottom: "0.5rem",
                }}
              >
                Due Date
              </p>
              <div className="a-filter-date-range">
                <span className="a-filter-date-field">
                  <input
                    type="date"
                    placeholder="Start Date"
                    defaultValue={
                      selectedFilters.get("startDate")
                        ? selectedFilters.get("startDate")[0]
                        : Date.now().toLocaleString()
                    }
                    onChange={(e) => toggleFilter("startDate", e.target.value)}
                  />
                </span>
                <span className="a-filter-date-field">
                  <input
                    type="date"
                    placeholder="End Date"
                    defaultValue={
                      selectedFilters.get("endDate")
                        ? selectedFilters.get("endDate")[0]
                        : Date.now().toLocaleString()
                    }
                    onChange={(e) => toggleFilter("endDate", e.target.value)}
                  />
                </span>
              </div>
            </section>
          )}

          <section className="a-filter-apply-sec">
            <button onClick={() => onApply(selectedFilters)}>Apply</button>
          </section>
        </div>
      </div>

      {showToast && (
        <Toast
          type="error"
          header={"Please enter a valid date range"}
          onClose={() => setShowToast(false)}
        />
      )}
    </>
  );
}

function getTypeClass(type) {
  switch (type) {
    case "chip":
      return "field-style chip";
    case "string":
      return "field-style string";
    default:
      return "";
  }
}

function getItemCategories(keys, item) {
  const categories = keys.map((cat) => item[cat]);

  return categories.map((cat, index) => {
    return (
      <p
        key={`${cat}-${index}`}
        className={`${index === categories.length - 1 ? "" : "category-border"}`}
      >
        {cat}
      </p>
    );
  });
}
